import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Seo } from '@/lib/seo';
import {
  Loader, Phone, Mail, ArrowRight, Warning, Check, WhatsApp,
} from '@/components/ui/Icons';
import { company } from '@/data/company';
import { findService, findSubService } from '@/data/services';

type Status = 'new' | 'contacted' | 'quoted' | 'won' | 'lost' | 'spam';
const STATUSES: Status[] = ['new', 'contacted', 'quoted', 'won', 'lost', 'spam'];

type Quote = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  service: string;
  status: Status;
  message: string;
  notes: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  updated_at: string;
};

type AuditEntry = {
  id: number;
  admin_email: string;
  action: string;
  meta: Record<string, unknown> | null;
  created_at: string;
};

type DetailResponse = { ok: true; quote: Quote; audit: AuditEntry[] };

export default function AdminQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery<DetailResponse>({
    queryKey: ['admin-quote', id],
    queryFn: () => api<DetailResponse>(`/admin/quotes/${id}`),
    enabled: !!id,
  });

  const [status, setStatus] = useState<Status>('new');
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);

  if (data && !touched) {
    if (status !== data.quote.status) setStatus(data.quote.status);
    if (notes !== (data.quote.notes ?? '')) setNotes(data.quote.notes ?? '');
  }

  const save = useMutation({
    mutationFn: (next: Partial<{ status: Status; notes: string }>) =>
      api<{ ok: true; quote: Quote }>(`/admin/quotes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(next),
      }),
    onSuccess: () => {
      setTouched(false);
      void qc.invalidateQueries({ queryKey: ['admin-quote', id] });
      void qc.invalidateQueries({ queryKey: ['admin-quotes'] });
      void qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api(`/admin/quotes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-quotes'] });
      void qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
      navigate('/admin/quotes');
    },
  });

  if (isLoading) {
    return <div className="flex items-center gap-2 text-ink-700"><Loader className="h-5 w-5" /> Loading…</div>;
  }
  if (isError || !data) {
    return <div className="text-red-700">Couldn&rsquo;t load this quote.</div>;
  }

  const q = data.quote;
  const serviceLabel = formatServiceLabel(q.service);
  const mailto = buildMailto(q, serviceLabel);
  const whatsappHref = q.phone ? `https://wa.me/${normalisePhone(q.phone)}?text=${encodeURIComponent(`Hi ${q.name.split(' ')[0]}, this is ${company.shortName} following up on your enquiry`)}` : null;

  return (
    <>
      <Seo title={`Quote #${q.id} - ${q.name}`} description="Quote detail" path={`/admin/quotes/${q.id}`} noindex />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link to="/admin/quotes" className="text-sm text-ink-700 hover:text-navy-800">
          ← Back to inbox
        </Link>
        <div className="flex items-center gap-2 text-xs text-ink-700">
          <span>Quote #{q.id}</span>
          <span>·</span>
          <span>Updated {new Date(q.updated_at).toLocaleString('en-GB')}</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-2xl border border-paper-100 bg-paper-0 p-6 shadow-card sm:p-7">
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-semibold text-navy-900 sm:text-3xl">
                {q.name}
              </h1>
              <p className="mt-1 text-sm text-ink-700">
                Received {new Date(q.created_at).toLocaleString('en-GB')}
              </p>
            </div>
            <StatusPill status={q.status} />
          </header>

          {/* Quick respond */}
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={mailto} className="btn-gold">
              <Mail className="h-4 w-4" /> Reply by email
            </a>
            {q.phone && (
              <a href={`tel:${normalisePhone(q.phone)}`} className="btn-outline-gold">
                <Phone className="h-4 w-4" /> {q.phone}
              </a>
            )}
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="btn-ghost">
                <WhatsApp className="h-4 w-4" /> WhatsApp
              </a>
            )}
            <button
              type="button"
              onClick={() => save.mutate({ status: 'contacted' })}
              disabled={save.isPending || q.status === 'contacted'}
              className="btn-ghost"
            >
              <Check className="h-4 w-4" /> Mark contacted
            </button>
            {q.status !== 'spam' ? (
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Mark "${q.name}" as spam?`)) save.mutate({ status: 'spam' });
                }}
                disabled={save.isPending}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
              >
                <Warning className="h-4 w-4" /> Flag spam
              </button>
            ) : (
              <button
                type="button"
                onClick={() => save.mutate({ status: 'new' })}
                disabled={save.isPending}
                className="btn-ghost"
              >
                <Check className="h-4 w-4" /> Not spam
              </button>
            )}
          </div>

          <dl className="mt-7 grid gap-4 sm:grid-cols-2">
            <Field label="Service">{serviceLabel}</Field>
            {q.address && <Field label="Address">{q.address}</Field>}
            <Field label="IP">{q.ip ?? '-'}</Field>
            <Field label="User agent" className="break-all">{q.user_agent ?? '-'}</Field>
          </dl>

          <div className="mt-7">
            <h2 className="font-display text-lg font-semibold text-navy-800">Message</h2>
            <p className="mt-2 whitespace-pre-wrap rounded-md border border-paper-100 bg-paper-50 p-4 text-ink-700">
              {q.message}
            </p>
          </div>

          <div className="mt-7 flex justify-end">
            <button
              type="button"
              onClick={() => {
                if (confirm(`Permanently delete this quote? This can't be undone.`)) remove.mutate();
              }}
              disabled={remove.isPending}
              className="text-sm font-semibold text-red-700 underline-offset-4 hover:underline"
            >
              {remove.isPending ? 'Deleting…' : 'Delete permanently'}
            </button>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-2xl border border-paper-100 bg-paper-0 p-6 shadow-card">
            <h2 className="font-display text-lg font-semibold text-navy-900">Status &amp; notes</h2>
            <label className="mt-4 block text-sm font-semibold text-navy-800">
              Status
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value as Status); setTouched(true); }}
                className="mt-1 block w-full rounded-md border border-paper-100 px-3 py-2 text-sm"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{capitalise(s)}</option>)}
              </select>
            </label>
            <label className="mt-4 block text-sm font-semibold text-navy-800">
              Notes <span className="font-normal text-xs text-ink-700">(admin only)</span>
              <textarea
                rows={5}
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setTouched(true); }}
                placeholder="Internal notes - won't be sent to the customer."
                className="mt-1 block w-full rounded-md border border-paper-100 px-3 py-2 text-sm"
              />
            </label>
            <button
              type="button"
              className="btn-gold mt-4 w-full"
              disabled={!touched || save.isPending}
              onClick={() => save.mutate({ status, notes })}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            {save.isError && <p className="mt-2 text-sm text-red-700">Save failed. Try again.</p>}
          </div>

          <div className="rounded-2xl border border-paper-100 bg-paper-0 p-6 shadow-card">
            <h2 className="font-display text-lg font-semibold text-navy-900">Activity</h2>
            {data.audit.length === 0 ? (
              <p className="mt-2 text-sm text-ink-700">No activity yet.</p>
            ) : (
              <ul className="mt-3 space-y-3 text-sm">
                {data.audit.map((a) => (
                  <li key={a.id} className="border-l-2 border-gold-300 pl-3">
                    <p className="font-semibold text-navy-800">{prettyAction(a.action)}</p>
                    {a.meta && (
                      <p className="mt-0.5 text-xs text-ink-700">{describeMeta(a.action, a.meta)}</p>
                    )}
                    <p className="mt-0.5 text-xs text-ink-700">
                      {a.admin_email} · {new Date(a.created_at).toLocaleString('en-GB')}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-xs uppercase tracking-wider text-ink-700/70">{label}</dt>
      <dd className="mt-0.5 text-navy-900">{children}</dd>
    </div>
  );
}

function StatusPill({ status }: { status: Status }) {
  const map: Record<Status, string> = {
    new: 'bg-gold-100 text-gold-700 border-gold-200',
    contacted: 'bg-blue-100 text-blue-800 border-blue-200',
    quoted: 'bg-purple-100 text-purple-800 border-purple-200',
    won: 'bg-green-100 text-green-800 border-green-200',
    lost: 'bg-red-100 text-red-800 border-red-200',
    spam: 'bg-paper-100 text-ink-700 border-paper-100',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${map[status]}`}>
      {status}
    </span>
  );
}

function buildMailto(q: Quote, serviceLabel: string): string {
  const subject = `Re: your ${serviceLabel} enquiry`;
  const body =
`Hi ${q.name.split(' ')[0]},

Thanks for getting in touch with ${company.shortName}.

Regarding your ${serviceLabel} enquiry, ${'we’d be happy to help.'}

Could you let us know:
  - Best time to call you back
  - Any photos of the area, if you have them

Many thanks,
${company.shortName}
${company.phone}
${company.url}`;
  return `mailto:${q.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function formatServiceLabel(slug: string): string {
  if (slug.includes('/')) {
    const [s, sub] = slug.split('/');
    const found = findSubService(s, sub);
    if (found) return `${found.service.navLabel} - ${found.sub.title}`;
  } else {
    const found = findService(slug);
    if (found) return found.navLabel;
  }
  return slug;
}

function normalisePhone(p: string): string {
  return p.replace(/[^\d+]/g, '');
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function prettyAction(a: string): string {
  return a.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function describeMeta(action: string, meta: Record<string, unknown>): string {
  if (action === 'status_change' && 'from' in meta && 'to' in meta) {
    return `${meta.from} → ${meta.to}`;
  }
  return Object.entries(meta).map(([k, v]) => `${k}: ${String(v)}`).join(', ');
}
